import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { requestService, scrollToId } from "@/lib/nav";

const headlineLines = ["Professionelle Gebäudereinigung", "für Bremen & Umgebung."];
const trust = ["Persönliche Beratung", "Transparentes Angebot", "Zuverlässige Ausführung"];

export const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 80]);

  return (
    <section id="start" ref={ref} className="relative pt-32 lg:pt-40 pb-16 lg:pb-24 bg-sand grain overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-6">
          <motion.p
            initial={reduce ? {} : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-forest bg-white border border-border rounded-full px-4 py-1.5 mb-6"
            data-testid="hero-eyebrow"
          >
            Gebäudeservice &amp; Objektpflege · Bremen
          </motion.p>

          <h1 className="font-heading font-extrabold tracking-tight leading-[1.14] text-4xl sm:text-5xl lg:text-6xl text-charcoal" data-testid="hero-heading">
            {headlineLines.map((line, i) => (
              <span key={line} className="line-mask">
                <motion.span
                  className="block pb-[0.14em]"
                  initial={reduce ? { y: 0 } : { y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === headlineLines.length - 1 ? <span className="text-forest">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={reduce ? {} : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 text-lg leading-relaxed text-charcoal-muted max-w-xl"
          >
            Zuverlässige Reinigung für Büros, Arztpraxen, Treppenhäuser und Gewerbeobjekte –
            regelmäßig oder als Einzelauftrag.
          </motion.p>

          <motion.p
            initial={reduce ? {} : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.66 }}
            className="mt-3 text-base text-charcoal-muted/90 max-w-xl"
          >
            Ergänzend bieten wir Objektpflege und ausgewählte Ausbauleistungen.
          </motion.p>

          <motion.div
            initial={reduce ? {} : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.72 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <button onClick={() => requestService("")} data-testid="hero-primary-cta" className="group inline-flex items-center justify-center gap-2 rounded-md bg-forest px-6 py-3.5 text-white font-semibold hover:bg-forest-dark hover:-translate-y-0.5 transition-[transform,background-color] duration-200">
              Kostenloses Angebot anfordern
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => scrollToId("#leistungen")} data-testid="hero-secondary-cta" className="inline-flex items-center justify-center rounded-md border border-forest/30 bg-white px-6 py-3.5 text-forest font-semibold hover:border-forest hover:bg-white transition-colors duration-200">
              Leistungen ansehen
            </button>
          </motion.div>

          <motion.ul
            initial={reduce ? {} : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-8 flex flex-wrap gap-x-6 gap-y-3"
          >
            {trust.map((t) => (
              <li key={t} className="inline-flex items-center gap-2 text-sm font-medium text-charcoal" data-testid="hero-trust">
                <CheckCircle2 className="w-4 h-4 text-gold" /> {t}
              </li>
            ))}
          </motion.ul>
        </div>

        <div className="lg:col-span-6">
          <div className="relative">
            <motion.div style={{ y: yImg }} className="relative rounded-2xl overflow-hidden border border-border shadow-[0_12px_40px_rgb(0,0,0,0.08)] aspect-[4/5] sm:aspect-[3/2] lg:aspect-[4/5]">
              <img
                src="https://images.unsplash.com/photo-1627905646269-7f034dcc5738?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                alt="Reinigungskraft mit Handschuhen reinigt einen Büroschreibtisch mit Sprühflasche und Tuch"
                className="w-full h-full object-cover"
                width="1400" height="1750" fetchPriority="high"
              />
            </motion.div>
            <div className="absolute -bottom-5 -left-4 sm:left-6 bg-white rounded-xl border border-border shadow-[0_12px_40px_rgb(0,0,0,0.08)] px-5 py-4 max-w-[15rem]" data-testid="hero-badge">
              <p className="font-heading font-bold text-forest text-lg leading-none">Bremen & Umgebung</p>
              <p className="text-sm text-charcoal-muted mt-1.5">Ein Ansprechpartner für Reinigung, Pflege und Ausbau.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
